import { StateObservable } from 'redux-observable'
import { AnyAction } from 'redux'
import { Observable } from 'rxjs'
import { map, debounceTime, mergeMap, filter } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { RootState } from 'src/state/types'
import { partyApiEvents, partyApiSelectors } from '../../api/party'
import { ContactsEvent, events as contactsEvents } from './state'
import { uiEvents } from 'modules/ui'
import { adhocInvoiceEvents } from 'modules/adhocInvoice/state'

/**
 * Spelled out rather than reusing `Epic<…>`, whose `Output extends Input` constraint cannot
 * express these: the input is the contacts action union — which is what lets `isOfType` narrow
 * `action.payload` below — but the output is other modules' events. `state/root.ts` casts each
 * epic namespace to `Epic[]` when it collects them, so the middleware is unaffected.
 */
type ContactsEpic = (action$: Observable<ContactsEvent>, state$: StateObservable<RootState>) => Observable<AnyAction>

export const searchPartiesOnSearchQueryChange: ContactsEpic = action$ =>
  action$.pipe(
    filter(isOfType(contactsEvents.searchQueryUpdated.type)),
    debounceTime(400),
    map(({ payload }) => partyApiEvents.quickSearch_request({ query: payload })),
    tag('module/contacts/searchPartiesOnSearchQueryChange'),
  )

export const openDepositTopupRequestModal: ContactsEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(contactsEvents.depositTopupRequestOpened.type)),
    map(({ payload }) => {
      const { partyId, portfolioId } = payload
      const state = state$.value
      const partyName = partyApiSelectors.getPartyNameById(state)(partyId)
      return {
        isDepositInvoice: true,
        customer: {
          id: partyId,
          text: partyName,
          partyTag: 'Tenant',
        },
        portfolioId,
        invoiceType: {
          label: 'Deposit Top Up',
          value: 'DepositTopUp',
        },
        dueDate: '',
        vat: false,
        amount: '',
        beneficiaries: [
          {
            beneficiary: {
              type: 'DepositBeneficiary',
              value: {
                partyId,
                reference: `${partyName} Deposit Top Up`,
                beneficiaryTag: 'TenantDepositAccount',
                amount: '',
                vat: false,
                transfer: true,
              },
            },
          },
        ],
      }
    }),
    mergeMap(initialValues => [
      uiEvents.redirect('/invoices/new'),
      adhocInvoiceEvents.initialValuesUpdateRequested(initialValues),
    ]),
    tag('module/contacts/openDepositTopupRequestModal'),
  )
